import { clearPartnerStorageItems } from '@in-life/utils/clearPartnerStorageItems';
import { useEffect } from 'react';

import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { ClientType, useAuthContext } from '@services/Auth';
import { useSetMode } from '@store/journey/selectors';
import { JOURNEY_MODE } from '@store/journey/slice';
import { getBasketIdFromUrl } from '@utils/routing';
import { getIsExistingCustomerFromUrl } from '@utils/routing/getIsExistingCustomerFromUrl';

/**
 * Resolves SIGNUP vs INLIFE from basket data before any journey router mounts.
 */
const JourneyModeResolver = () => {
  const setMode = useSetMode();
  const { basketId, handleBasketCreated, basketGuardData, basketGuardLoading } =
    useBasketContext();
  const idFromUrl = getBasketIdFromUrl();
  const { clients } = useAuthContext();
  const hasIsExistingCustomerParam = getIsExistingCustomerFromUrl();

  // If the URL has the isExistingCustomer=1 flag, logout the auth clients and clear the partner storage items
  // This does not control the journey mode, that is determined by the basket data.
  useEffect(() => {
    if (hasIsExistingCustomerParam) {
      const clientsToLogout = [
        clients[ClientType.OktaExternal],
        clients[ClientType.OktaInternal],
      ].filter((client) => client?.isAuthenticated);

      clientsToLogout.forEach((client) => {
        void client?.logout();
      });

      clearPartnerStorageItems();
    }
  }, [hasIsExistingCustomerParam, clients]);

  useEffect(() => {
    if (!idFromUrl || idFromUrl === basketId) {
      return;
    }

    handleBasketCreated(idFromUrl);
  }, [idFromUrl, basketId, handleBasketCreated]);

  useEffect(() => {
    const resolvedBasketId = basketId || idFromUrl;

    if (!resolvedBasketId) {
      setMode(JOURNEY_MODE.SIGNUP);
      return;
    }

    if (basketGuardLoading || !basketId) {
      return;
    }

    const basketHasInLifeFlag =
      basketGuardData?.basket?.details?.isExistingCustomer;

    if (basketHasInLifeFlag) {
      clearPartnerStorageItems();
    }

    setMode(basketHasInLifeFlag ? JOURNEY_MODE.INLIFE : JOURNEY_MODE.SIGNUP);
  }, [
    basketId,
    idFromUrl,
    basketGuardLoading,
    basketGuardData,
    setMode,
    clients,
  ]);

  return null;
};

export { JourneyModeResolver };
